/**
 * Derivation layer — reads xfactor's digest envelope into view models.
 *
 * ── The important discovery ───────────────────────────────────────────────
 * The envelope is not raw section data that a client has to compose. xfactor's
 * synthesis pass writes two **pre-composed artifacts** into `summary`:
 *
 *   summary.situationCard   → header, scoreStrip, mustKnow, watchOuts, moves,
 *                             outlook, meetingOneLiner
 *   summary.executiveBrief  → situationAtAGlance, customerWorld, businessHealth,
 *                             whatsChanging, landmines, relationshipImplications,
 *                             playbook, stakeholderMap, openQuestions
 *
 * That is why xfactor documents these views as costing "no additional LLM
 * calls" — the artifacts already exist in the payload. So this module *reads*
 * them rather than re-deriving them from sections. Deriving would produce a
 * different, worse answer than the one the synthesis pass already committed to.
 *
 * Field names below were read off a real Bank of America digest, not guessed.
 * Alternate spellings are still tolerated where an older payload might differ,
 * but the first candidate in each list is the confirmed one.
 */

import type {
  XFactorCitation,
  XFactorDigestData,
  XFactorSection,
  XFactorSummary,
} from "../../../Types/XFactorBriefing";
import type {
  XFactorAccountSynopsis,
  XFactorBand,
  XFactorBriefingCard,
  XFactorBubbleRow,
  XFactorDecisionAttribute,
  XFactorDisc,
  XFactorExecProfile,
  XFactorExecutiveBrief,
  XFactorExplainPane,
  XFactorFullBriefing,
  XFactorHeadlineScores,
  XFactorMove,
  XFactorPoint,
  XFactorPortfolioAccount,
  XFactorRailStop,
  XFactorRoPoint,
  XFactorScoreTile,
  XFactorSectionBubble,
  XFactorSectionDetail,
  XFactorStakeholder,
} from "../../../Types/XFactorViewsRendering";
import type { BusinessHealthThresholds } from "../../../Utils/HealthThresholds";
import { businessHealthClass } from "../../../Utils/Helper/businessHealth";
import { toScore, toText } from "../../XFactorBriefings/components/Primitives";

/* ------------------------------------------------------------------ */
/* Section identity                                                     */
/* ------------------------------------------------------------------ */

/** xfactor's display order — rail, matrix columns, Full Briefing tabs. */
/**
 * Column and tab order, matching EB's existing Public Data chart exactly —
 * `sectionOrder` in `Container/WatchlistSummary/dataAdapter.ts`. The two views
 * sit side by side under Always On Intelligence, so a reader moving between
 * them should not have to re-find the columns.
 *
 * Sections xfactor scores but EB's chart has no counterpart for go at the end
 * rather than in xfactor's own position, so the shared prefix stays aligned.
 */
export const VIEW_SECTION_ORDER = [
  "about",
  "financial",
  "news",
  "leadership",
  "legal",
  "social",
  "traffic",
  "growth",
  "stack",
  // No counterpart in EB's chart — appended.
  "initiatives",
] as const;

/** Short labels — section rail, Full Briefing tabs, card headers. */
export const SECTION_LABEL: Record<string, string> = {
  summary: "Summary",
  about: "About",
  news: "News",
  financial: "Financial",
  growth: "Growth",
  social: "Social",
  traffic: "Traffic",
  legal: "Legal",
  stack: "Stack",
  initiatives: "Initiatives",
  leadership: "Leadership",
};

/** Long labels — bubble matrix column headers. */
export const SECTION_COLUMN_LABEL: Record<string, string> = {
  summary: "Overall",
  about: "About Company",
  news: "News Analysis",
  financial: "Financial Overview",
  growth: "Growth Signals",
  social: "Social Media",
  traffic: "Digital Traffic",
  legal: "Legal & Compliance",
  stack: "Technology Stack",
  // No counterpart in EB's existing Public Data chart. Kept rather than
  // dropped: xfactor scores a strategic-initiatives section, and hiding a
  // column would hide the data, not reconcile it.
  initiatives: "Strategic Initiatives",
  leadership: "Leadership Team",
};

/**
 * Section detail modal titles, read off xfactor by walking its own
 * "Prev section" control. These are not derivable from the section id — seven
 * of the eleven differ from the obvious guess.
 */
export const SECTION_TITLE: Record<string, string> = {
  summary: "Executive Summary",
  about: "About the Company",
  news: "Recent News Analysis",
  financial: "Financial Information",
  growth: "Growth Signals",
  social: "Social Media Sentiment",
  traffic: "Digital Traffic Patterns",
  legal: "Legal & Regulatory Compliance",
  stack: "Technology Stack",
  initiatives: "Strategic Organizational Initiatives",
  leadership: "Leadership Team",
};

/* ------------------------------------------------------------------ */
/* Readers                                                              */
/* ------------------------------------------------------------------ */

type Dict = Record<string, unknown>;

export const isDict = (v: unknown): v is Dict =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const pick = (obj: unknown, keys: string[]): unknown => {
  if (!isDict(obj)) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (v != null && v !== "") return v;
  }
  return undefined;
};

export const pickText = (obj: unknown, keys: string[]): string | null =>
  toText(pick(obj, keys));

/**
 * Drop inline `[c1][c7]` evidence markers from prose.
 *
 * xfactor threads them through generated text and strips them at render,
 * hanging the evidence off the block instead. Anywhere the marker survives it
 * reads as a typo rather than a citation. Also tidies the space the removal
 * leaves behind, including before punctuation.
 */
export const stripRefMarkers = (text?: string | null): string | null => {
  if (!text) return text ?? null;
  const cleaned = text
    .replace(/\[c\d+\]/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .trim();
  return cleaned || null;
};

export const pickScore = (obj: unknown, keys: string[]): number | null =>
  toScore(pick(obj, keys));

const UNITS: Array<[number, string]> = [
  [1e12, "T"],
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "K"],
];

const abbreviate = (n: number): string => {
  for (const [size, suffix] of UNITS) {
    if (n >= size) {
      // Two decimals, then trimmed: 179270000000 → 179.27B, 318512 → 319K.
      const scaled = n / size;
      const digits = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
      return `${parseFloat(scaled.toFixed(digits))}${suffix}`;
    }
  }
  return String(n);
};

/**
 * Make raw model-emitted numbers readable — `179270000000 USD` → `$179.27B`,
 * `318512` → `319K`.
 *
 * Only integers of **six digits or more** are touched. That is the guard that
 * makes this safe to run over prose: years (`2025`), quarters and small counts
 * all fall below the threshold and pass through untouched. A trailing currency
 * code is folded into a leading symbol rather than printed twice.
 */
export const humanizeNumbers = (text?: string | null): string | null => {
  if (!text) return text ?? null;
  return text.replace(
    /(\$\s*)?\b(\d{6,})\b(\s*(USD|EUR|GBP))?/gi,
    (whole, symbol, digits, _tail, code) => {
      const n = Number(digits);
      if (!Number.isFinite(n)) return whole;
      const money = Boolean(symbol) || Boolean(code);
      return `${money ? "$" : ""}${abbreviate(n)}`;
    }
  );
};

export const pickList = (obj: unknown, keys: string[]): unknown[] => {
  if (!isDict(obj)) return [];
  for (const k of keys) {
    const v = obj[k];
    if (Array.isArray(v) && v.length) return v;
  }
  return [];
};

const asStrings = (value: unknown): string[] =>
  Array.isArray(value) ? (value.map(toText).filter(Boolean) as string[]) : [];

const refsOf = (item: unknown): string[] =>
  asStrings(pick(item, ["evidence_refs", "citation_refs", "refs"]));

/** `source` on an executive is a single citation id, not a list. */
const singleRef = (item: unknown): string[] => {
  const s = pickText(item, ["source"]);
  return s ? [s] : [];
};

/* ------------------------------------------------------------------ */
/* Bands                                                                */
/* ------------------------------------------------------------------ */

/**
 * Map a 0–100 score onto R/Y/G bands using vendor `health_score_thresholds`
 * (Business Health). Defaults match Settings product bands when omitted.
 *
 * Strong → green · Stable → yellow · Concern → red · else unknown (grey).
 */
export const bandOf = (
  score: number | null | undefined,
  thresholds?: Partial<BusinessHealthThresholds> | null
): XFactorBand => {
  const band = businessHealthClass(score, thresholds);
  if (band === "Strong") return "strong";
  if (band === "Stable") return "moderate";
  if (band === "Concern") return "weak";
  return "unknown";
};

/**
 * Risk is NOT inverted.
 *
 * This looks wrong and is not: xfactor bands every number the same way,
 * including risk. Measured on the live portfolio, risk 38 renders red and risk
 * 79 renders amber — the opposite of an inverted scale — and the Situation Card
 * shows RISK 43 in red. Its "risk" score reads as a posture score where higher
 * is better, so inverting it here would flip the colours against the source.
 *
 * Kept as a named alias rather than deleted so the intent stays greppable.
 */
export const riskBandOf = bandOf;

/** Band bases, measured off xfactor's computed styles (Tailwind 500 ramp). */
export const BAND_FILL: Record<XFactorBand, string> = {
  strong: "#22c55e",
  moderate: "#e9b409",
  weak: "#ef4444",
  unknown: "#cbd5e1",
};

/* ------------------------------------------------------------------ */
/* Envelope access                                                      */
/* ------------------------------------------------------------------ */

export const getSummary = (
  data?: XFactorDigestData | null
): XFactorSummary | null =>
  isDict(data) && isDict(data.summary)
    ? (data.summary as XFactorSummary)
    : null;

export const getSection = (
  data: XFactorDigestData | null | undefined,
  sectionId: string
): XFactorSection | null => {
  if (!isDict(data)) return null;
  const raw = data[sectionId];
  return isDict(raw) ? (raw as XFactorSection) : null;
};

/** The pre-composed Situation Card artifact. */
const situationCardOf = (data?: XFactorDigestData | null): Dict | null => {
  const sc = (getSummary(data) as Dict | null)?.situationCard;
  return isDict(sc) ? sc : null;
};

/** The pre-composed Executive Brief artifact. */
const executiveBriefOf = (data?: XFactorDigestData | null): Dict | null => {
  const eb = (getSummary(data) as Dict | null)?.executiveBrief;
  return isDict(eb) ? eb : null;
};

/**
 * Envelope keys that are synthesis or run metadata rather than content
 * sections. `summary_meta` is token/cost accounting; counting it as a section
 * would give it a rail stop, a matrix column and an empty Full Briefing tab.
 */
const NON_SECTION_KEYS = new Set([
  "summary",
  "summary_meta",
  "synthesis",
  "meta",
  "metadata",
  "config",
  "totals",
]);

const isNonSectionKey = (key: string): boolean =>
  NON_SECTION_KEYS.has(key) ||
  key.startsWith("_") ||
  key.startsWith("$") ||
  key.endsWith("_meta");

export const presentSectionIds = (
  data?: XFactorDigestData | null
): string[] => {
  const known = VIEW_SECTION_ORDER as readonly string[];
  if (!isDict(data)) return [...known];
  const extra = Object.keys(data).filter(
    k => !isNonSectionKey(k) && !known.includes(k) && isDict(data[k])
  );
  return [...known, ...extra.sort()];
};

/**
 * Section keys that actually arrived in the digest.
 *
 * Do not use `presentSectionIds` for this census — that always includes the
 * ten matrix columns so hollow bubbles still render.
 */
export const arrivedSectionIds = (
  data?: XFactorDigestData | null
): string[] => {
  if (!isDict(data)) return [];
  const known = VIEW_SECTION_ORDER as readonly string[];
  const arrived = known.filter(k => isDict(data[k]));
  const extra = Object.keys(data).filter(
    k => !isNonSectionKey(k) && !known.includes(k) && isDict(data[k])
  );
  return [...arrived, ...extra.sort()];
};

/** Expected product sections that did not arrive (presence, not failure). */
export const missingSectionIds = (arrived: string[]): string[] => {
  const present = new Set(arrived.map(id => String(id).toLowerCase()));
  return VIEW_SECTION_ORDER.filter(id => !present.has(id));
};

/** Envelope keys this port does not read — surfaced as a UI diagnostic. */
export const unmappedKeys = (data?: XFactorDigestData | null): string[] => {
  if (!isDict(data)) return [];
  const known = new Set<string>([...VIEW_SECTION_ORDER, "summary"]);
  return Object.keys(data).filter(k => !known.has(k) && !isNonSectionKey(k));
};

/**
 * The five headline scores.
 *
 * `executiveBrief.situationAtAGlance.scores` is authoritative — it is the only
 * place carrying `overall`. The Situation Card's `scoreStrip` has the other
 * four, and the summary's own `sectionSummary` is the last resort.
 *
 * ── A known, deliberate difference from xfactor's portfolio table ─────────
 * The summary's `sectionScore.value` is null, explained as "computed by
 * application from health/fit/opportunity/risk using sectionCompositeWeights".
 * Those weights live in xfactor's org config (`pub_briefing_org_aiconfig`),
 * which only its server functions can read — there is no admin endpoint for
 * them, so EB cannot reproduce that composite.
 *
 * We therefore show the payload's own numbers. They match xfactor's Situation
 * Card and Executive Brief exactly, but its *portfolio table* can show a
 * different Overall and Risk for the same account, because that table applies
 * the org weights. Reproducing it needs the weights exposed upstream.
 */
export const headlineScores = (
  data?: XFactorDigestData | null
): XFactorHeadlineScores => {
  const glance = executiveBriefOf(data)?.situationAtAGlance;
  const scores = isDict(glance) ? glance.scores : undefined;
  const strip = situationCardOf(data)?.scoreStrip;
  const ss = (getSummary(data) as Dict | null)?.sectionSummary;

  const summary = getSummary(data) as Dict | null;

  const read = (
    name: string,
    legacyKey: string,
    sectionKey: string
  ): number | null => {
    for (const [source, key] of [
      [scores, name],
      [strip, name],
      // Older digests predate the synthesis artifacts and carry the numbers
      // flat on `summary`. Both generations are live, so both are read.
      [summary, legacyKey],
      [ss, sectionKey],
    ] as Array<[unknown, string]>) {
      const value = pickScore(source, [key]);
      if (value != null) return value;
    }
    return null;
  };

  return {
    overall: read("overall", "overallScore", "sectionScore"),
    health: read("health", "overallHealthScore", "sectionHealthScore"),
    fit: read("fit", "overallRelevanceScore", "sectionRelevanceScore"),
    opportunity: read(
      "opportunity",
      "overallOpportunityScore",
      "sectionOpportunityScore"
    ),
    risk: read("risk", "overallRiskScore", "sectionRiskScore"),
  };
};

/**
 * Every citation id an artifact actually leans on — `evidence_refs` at any
 * depth, plus inline `[c12]` markers inside prose.
 */
const refsUsedBy = (node: unknown): Set<string> => {
  const found = new Set<string>();
  const walk = (o: unknown): void => {
    if (Array.isArray(o)) {
      o.forEach(walk);
      return;
    }
    if (isDict(o)) {
      Object.entries(o).forEach(([key, value]) => {
        if (
          Array.isArray(value) &&
          ["evidence_refs", "citation_refs", "refs"].includes(key)
        ) {
          value.forEach(v => {
            const t = toText(v);
            if (t) found.add(t);
          });
        } else {
          walk(value);
        }
      });
      return;
    }
    if (typeof o === "string") {
      const matches = o.match(/\[(c\d+)\]/g);
      if (matches) matches.forEach(m => found.add(m.slice(1, -1)));
    }
  };
  walk(node);
  return found;
};

/**
 * The sources an artifact cites, in citation order.
 *
 * xfactor's SOURCES count is per artifact, not per digest: the Situation Card
 * for JPMorgan shows 29 and the Executive Brief 50, while `summary.citations`
 * holds 105. Listing all 105 under either one both overstates the count and
 * pads the list with sources that block never used.
 */
export const citationsUsedBy = (
  artifact: unknown,
  all: XFactorCitation[]
): XFactorCitation[] => {
  const used = refsUsedBy(artifact);
  if (!used.size) return [];
  return all.filter(c => c?.id && used.has(c.id));
};

export const citationsOf = (
  node: XFactorSection | XFactorSummary | null | undefined
): XFactorCitation[] => {
  const list = pickList(node, ["citations"]);
  return list.filter(isDict) as unknown as XFactorCitation[];
};

/** Risk / opportunity signal lists. The `key…` prefix is the confirmed spelling. */
const riskSignals = (node: unknown): unknown[] =>
  pickList((node as Dict)?.keyRisksAndOpportunities, [
    "keyRiskSignalsRelevantToVendor",
    "riskSignalsRelevantToVendor",
  ]);

const opportunitySignals = (node: unknown): unknown[] =>
  pickList((node as Dict)?.keyRisksAndOpportunities, [
    "keyOpportunitySignalsRelevantToVendor",
    "opportunitySignalsRelevantToVendor",
  ]);

/* ------------------------------------------------------------------ */
/* Item coercion                                                        */
/* ------------------------------------------------------------------ */

/**
 * Any list entry → a renderable point.
 *
 * Confirmed shapes this covers:
 *   keyHighlights/keyLowlights  { text, explanation, confidence, evidence_refs }
 *   risk signal                 { signal, rationale, severity, business_implication }
 *   opportunity signal          { signal, rationale, strength, business_implication }
 *   mustKnow / watchOuts        { label, detail, source_section, evidence_refs }
 *   whatsChanging               { title, change, whyItMatters, category }
 *   landmine                    { risk, soWhat, status, source_section }
 *   openQuestion                { question, why, source_section }
 *   segment                     { segment, role, vendorRelevance }
 *   keyNumber                   { signal, reading }
 *   strategicInitiative         { name, description, status }
 */
export const asPoint = (item: unknown): XFactorPoint => {
  if (typeof item === "string") {
    return {
      title: humanizeNumbers(item),
      body: null,
      tag: null,
      chip: null,
      implication: null,
      outcome: null,
      timeframe: null,
      refs: [],
    };
  }
  return {
    // Model-emitted prose carries raw magnitudes and inline evidence markers;
    // both are cleaned here so every surface that renders a point benefits.
    title: humanizeNumbers(
      stripRefMarkers(
        pickText(item, [
          "text",
          "label",
          "title",
          "signal",
          "risk",
          "question",
          "segment",
          "point",
          "name",
          "action",
          "play",
          "item",
          "headline",
        ])
      )
    ),
    body: humanizeNumbers(
      stripRefMarkers(
        pickText(item, [
          "explanation",
          "detail",
          "rationale",
          "whyItMatters",
          "soWhat",
          "why",
          "reading",
          "change",
          // A segment's `role` is the em-dash suffix xfactor prints
          // ("Consumer & Community Banking — business line"), not a tag.
          "role",
          "description",
          "whyNot",
          "summary",
        ])
      )
    ),
    tag: pickText(item, ["source_section", "category", "type", "kind"]),
    chip: pickText(item, [
      "confidence",
      "severity",
      "strength",
      "priority",
      "status",
    ]),
    implication: pickText(item, [
      "business_implication",
      "businessImplication",
      // A segment's relevance to the vendor — xfactor labels this
      // "Relevance to us:". It is a separate line, not part of the body.
      "vendorRelevance",
    ]),
    outcome: pickText(item, ["expected_outcome", "expectedOutcome"]),
    timeframe: pickText(item, ["timeframe", "when"]),
    refs: refsOf(item),
  };
};

/**
 * Any list entry → a recommended play.
 *
 * Confirmed shapes:
 *   situationCard.moves     { play, when, owner, source_section, evidence_refs }
 *   executiveBrief.playbook { action, when, owner, rank, trigger, rationale,
 *                             expectedOutcome, evidence_refs }
 *   section keyActions      { action, priority, timeframe, action_score,
 *                             expected_outcome, evidence_refs }
 */
/**
 * Raw action horizons → the three buckets xfactor labels them with.
 *
 * The payload carries mixed vocabularies for the same idea — `now`, `30d`,
 * `60d`, `quarter`, `h1` — and xfactor collapses them to Now / Next / Later
 * rather than printing the raw token. Anything unrecognised is passed through
 * capitalised, so a new horizon shows up readable instead of disappearing.
 */
export const horizonLabel = (raw?: string | null): string | null => {
  const value = (raw || "").trim().toLowerCase();
  if (!value) return null;
  if (/^(now|immediate|asap|0d|today|this week)$/.test(value)) return "Now";
  if (/^(next|30d|30 days?|1m|month|this month|near[- ]?term)$/.test(value)) {
    return "Next";
  }
  if (
    /^(later|60d|90d|quarter|qtr|this quarter|h1|h2|long[- ]?term)$/.test(value)
  ) {
    return "Later";
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const asMove = (item: unknown): XFactorMove => ({
  timeframe: pickText(item, ["when", "timeframe", "horizon"]),
  action: pickText(item, ["play", "action", "text", "title", "recommendation"]),
  rationale: pickText(item, [
    "rationale",
    "action_explanation",
    "explanation",
    "why",
  ]),
  owner: pickText(item, [
    "owner",
    "recommended_assignee",
    "recommendedAssignee",
    "assignee",
  ]),
  outcome: pickText(item, [
    "expectedOutcome",
    "expected_outcome",
    "outcome",
    "impact",
  ]),
  trigger: pickText(item, ["trigger", "because"]),
  tag: pickText(item, ["source_section", "action_category", "category"]),
  priority: pickText(item, ["priority", "urgency"]),
  score: pickScore(item, ["action_score", "actionScore", "score"]),
});

export const pointsFrom = (value: unknown): XFactorPoint[] =>
  (Array.isArray(value) ? value : [])
    .map(asPoint)
    .filter(p => p.title || p.body);

export const movesFrom = (value: unknown): XFactorMove[] =>
  (Array.isArray(value) ? value : []).map(asMove).filter(m => m.action);

/* ------------------------------------------------------------------ */
/* Bubbles                                                              */
/* ------------------------------------------------------------------ */

/** Colour from section health, diameter from section relevance (Fit). */
export const renderSectionBubble = (
  sectionId: string,
  section: XFactorSection | null,
  label: string
): XFactorSectionBubble => {
  const ss = (section as Dict | null)?.sectionSummary;
  const health = pickScore(ss, ["sectionHealthScore"]);
  const relevance = pickScore(ss, ["sectionRelevanceScore", "orgRelevance"]);

  return {
    sectionId,
    label,
    score: health,
    band: bandOf(health),
    // Floor at 0.35 so a low-relevance section stays clickable.
    size: relevance == null ? 0.35 : 0.35 + (relevance / 100) * 0.65,
    relevance,
    synopsis: pickText(ss, ["synopsis"]),
    riskCount: riskSignals(section).length,
    opportunityCount: opportunitySignals(section).length,
    risk: pickScore(ss, ["sectionRiskScore"]),
    opportunity: pickScore(ss, ["sectionOpportunityScore"]),
    confidence: pickScore(
      isDict((section as Dict | null)?.commonMetadata)
        ? ((section as Dict).commonMetadata as Dict)
        : null,
      ["confidence_score"]
    ),
    missing: section == null,
  };
};

export const renderRow = (
  account: XFactorPortfolioAccount,
  data: XFactorDigestData | null | undefined,
  sectionIds: string[]
): XFactorBubbleRow => {
  const scores = data ? headlineScores(data) : account.scores;
  const generating = !data;
  return {
    digestId: account.digest_id,
    accountId: String(account.account_id || "").trim(),
    accountName: account.account_name || "Unknown account",
    accountMeta: account.digest_date || null,
    overall: {
      sectionId: "summary",
      label: SECTION_COLUMN_LABEL.summary,
      score: scores.overall ?? scores.health,
      band: bandOf(scores.overall ?? scores.health),
      size: scores.fit == null ? 0.35 : 0.35 + (scores.fit / 100) * 0.65,
      relevance: scores.fit,
      synopsis: pickText((getSummary(data) as Dict | null)?.sectionSummary, [
        "synopsis",
      ]),
      riskCount: riskSignals(getSummary(data)).length,
      opportunityCount: opportunitySignals(getSummary(data)).length,
      risk: scores.risk,
      opportunity: scores.opportunity,
      confidence: pickScore((getSummary(data) as Dict | null)?.commonMetadata, [
        "confidence_score",
      ]),
      missing: !data,
      generating,
    },
    bubbles: sectionIds.map(id => ({
      ...renderSectionBubble(
        id,
        getSection(data, id),
        SECTION_COLUMN_LABEL[id] || SECTION_LABEL[id] || id
      ),
      generating,
    })),
  };
};

/** Stable multi-key sort; the name tie-break stops rows jittering on ties. */
export const sortRows = <T extends { accountName: string }>(
  rows: T[],
  valueOf: (row: T) => number | null,
  direction: "asc" | "desc"
): T[] =>
  [...rows].sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (va == null && vb == null)
      return a.accountName.localeCompare(b.accountName);
    if (va == null) return 1;
    if (vb == null) return -1;
    if (va !== vb) return direction === "asc" ? va - vb : vb - va;
    return a.accountName.localeCompare(b.accountName);
  });

export const buildRoPoints = (
  accounts: XFactorPortfolioAccount[],
  thresholds?: Partial<BusinessHealthThresholds> | null
): XFactorRoPoint[] =>
  accounts
    .filter(a => a.scores.risk != null && a.scores.opportunity != null)
    .map(a => ({
      digestId: a.digest_id,
      accountName: a.account_name || "Unknown account",
      risk: a.scores.risk as number,
      opportunity: a.scores.opportunity as number,
      band: bandOf(a.scores.overall, thresholds),
      synopsis: null,
    }));

/* ------------------------------------------------------------------ */
/* Step 1 — Situation Card (read, not derived)                          */
/* ------------------------------------------------------------------ */

export const buildSituationCard = (
  accountName: string,
  data?: XFactorDigestData | null
) => {
  const summary = getSummary(data) as Dict | null;
  const card = situationCardOf(data);
  const strip = isDict(card?.scoreStrip) ? (card as Dict).scoreStrip : null;
  const header = isDict(card?.header) ? (card as Dict).header : null;
  const scores = headlineScores(data);

  // No Fit tile: the Situation Card leads with health, risk and opportunity.
  // `scores.fit` still drives bubble diameter elsewhere, so it stays parsed.
  const tiles: XFactorScoreTile[] = [
    { label: "Health", value: scores.health, rationale: null },
    { label: "Risk", value: scores.risk, rationale: null },
    { label: "Opportunity", value: scores.opportunity, rationale: null },
  ];

  return {
    accountName,
    freshness: pickText(header, ["freshness"]),
    confidence:
      pickScore(header, ["confidencePct", "confidence"]) ??
      pickScore(summary?.commonMetadata, ["confidence_score"]),
    tiles,
    // The synthesis pass explains the strip; that prose is the narrative.
    narrative:
      pickText(strip, ["whyTheseScores"]) ||
      pickText(summary?.sectionSummary, ["synopsis"]),
    quote: pickText(card, ["meetingOneLiner"]),
    mustKnow: pointsFrom(pickList(card, ["mustKnow"])),
    watchOuts: pointsFrom(pickList(card, ["watchOuts"])),
    moves: movesFrom(pickList(card, ["moves"])),
    outlook: pickText(card, ["outlook"]),
    citations: citationsUsedBy(
      card,
      citationsOf(summary as XFactorSummary | null)
    ),
    /** False when the synthesis pass produced no Situation Card at all. */
    present: card != null,
  };
};

/* ------------------------------------------------------------------ */
/* Step 2 — Executive Brief (read, not derived)                         */
/* ------------------------------------------------------------------ */

export const buildExecutiveBrief = (
  accountName: string,
  data?: XFactorDigestData | null
): XFactorExecutiveBrief => {
  const summary = getSummary(data) as Dict | null;
  const eb = executiveBriefOf(data);
  const glance = isDict(eb?.situationAtAGlance)
    ? ((eb as Dict).situationAtAGlance as Dict)
    : null;
  const cw = isDict(eb?.customerWorld)
    ? ((eb as Dict).customerWorld as Dict)
    : null;
  const bh = isDict(eb?.businessHealth)
    ? ((eb as Dict).businessHealth as Dict)
    : null;
  const lm = isDict(eb?.landmines) ? ((eb as Dict).landmines as Dict) : null;
  const ri = isDict(eb?.relationshipImplications)
    ? ((eb as Dict).relationshipImplications as Dict)
    : null;
  const scores = headlineScores(data);

  // No Fit tile — see buildSituationCard.
  const tiles: XFactorScoreTile[] = [
    { label: "Overall", value: scores.overall, rationale: null },
    { label: "Health", value: scores.health, rationale: null },
    { label: "Risk", value: scores.risk, rationale: null },
    { label: "Opportunity", value: scores.opportunity, rationale: null },
  ];

  return {
    accountName,
    present: eb != null,
    tiles,
    glance: pickText(glance, ["headline"]),
    whyTheseScores: pickText(glance, ["whyTheseScores"]),
    relationshipStage: pickText(glance, ["relationshipStage"]),
    dataAsOf: pickText(glance, ["freshnessDate"]),
    customerWorld: cw
      ? {
          synopsis: pickText(cw, ["text"]),
          whyTheyExist: pickText(cw, ["whyTheyExist"]),
          strategicPosture: pickText(cw, ["strategicPosture"]),
          items: pointsFrom(pickList(cw, ["segments"])),
        }
      : null,
    businessHealth: bh
      ? {
          trend: pickText(bh, ["trend"]),
          synopsis: pickText(bh, ["text"]),
          items: pointsFrom(pickList(bh, ["keyNumbers"])),
          forward: pickText(bh, ["forwardIndicators"]),
        }
      : null,
    whatsChanging: pointsFrom(pickList(eb, ["whatsChanging"])),
    landmines: {
      intro: pickText(lm, ["posture"]),
      /** True when the synthesis pass explicitly found nothing material. */
      none: lm?.noMaterialRisks === true,
      items: pointsFrom(pickList(lm, ["items"])),
    },
    relationship: ri
      ? {
          motion: pickText(ri, ["recommendedMotion"]),
          urgency: pickText(ri, ["urgency"]),
          summary: pickText(ri, ["whatItMeans"]),
          detail: pickText(ri, ["motionRationale"]),
          bullets: pickList(ri, ["valueConcentration"])
            .map(v => pickText(v, ["point"]))
            .filter(Boolean) as string[],
          caution: pickText(ri, ["caution"]),
        }
      : null,
    moves: movesFrom(pickList(eb, ["playbook"])),
    /** Plays the synthesis pass considered and rejected, with the reason. */
    droppedMoves: pointsFrom(pickList(eb, ["playbookDroppedItems"])),
    stakeholders: buildStakeholders(pickList(eb, ["stakeholderMap"])),
    openQuestions: pointsFrom(pickList(eb, ["openQuestions"])),
    citations: citationsUsedBy(
      eb,
      citationsOf(summary as XFactorSummary | null)
    ),
  };
};

const buildStakeholders = (people: unknown[]): XFactorStakeholder[] =>
  people
    .map(p => ({
      name: pickText(p, ["name", "full_name"]),
      title: pickText(p, ["title"]),
      role: pickText(p, ["role"]),
      status: pickText(p, ["engagement"]),
      summary: pickText(p, ["whyTheyMatter"]),
      angle: pickText(p, ["engagementAngle"]),
    }))
    .filter(p => p.name);

/* ------------------------------------------------------------------ */
/* Step 3 — Account Synopsis                                            */
/* ------------------------------------------------------------------ */

export const buildAccountSynopsis = (
  accountName: string,
  data?: XFactorDigestData | null
): XFactorAccountSynopsis => {
  const summary = getSummary(data) as Dict | null;

  const rail: XFactorRailStop[] = ["summary", ...presentSectionIds(data)].map(
    id => {
      if (id === "summary") {
        const health = headlineScores(data).health;
        return {
          sectionId: "summary",
          label: SECTION_LABEL.summary,
          band: bandOf(health),
          score: health,
          missing: summary == null,
        };
      }
      const section = getSection(data, id);
      const health = pickScore((section as Dict | null)?.sectionSummary, [
        "sectionHealthScore",
      ]);
      return {
        sectionId: id,
        label: SECTION_LABEL[id] || id,
        band: bandOf(health),
        score: health,
        missing: section == null,
      };
    }
  );

  return {
    accountName,
    rail,
    highlights: pointsFrom(pickList(summary, ["keyHighlights"])),
    lowlights: pointsFrom(pickList(summary, ["keyLowlights"])),
    opportunitiesAndRisks: pointsFrom([
      ...opportunitySignals(summary),
      ...riskSignals(summary),
    ]),
    recommendations: pointsFrom(pickList(summary, ["keyActions"])),
  };
};

/* ------------------------------------------------------------------ */
/* Section detail + full briefing                                       */
/* ------------------------------------------------------------------ */

export const getSectionDetail = (
  sectionId: string,
  data?: XFactorDigestData | null
): XFactorSectionDetail => {
  const isSummary = sectionId === "summary";
  const node = (
    isSummary ? getSummary(data) : getSection(data, sectionId)
  ) as Dict | null;
  const ss = isDict(node?.sectionSummary)
    ? (node as Dict).sectionSummary
    : null;

  const scores = isSummary
    ? (() => {
        const h = headlineScores(data);
        return [
          { label: "Health", value: h.health },
          { label: "Fit", value: h.fit },
          { label: "Opportunity", value: h.opportunity },
          { label: "Risk", value: h.risk },
        ];
      })()
    : [
        { label: "Health", value: pickScore(ss, ["sectionHealthScore"]) },
        { label: "Fit", value: pickScore(ss, ["sectionRelevanceScore"]) },
        {
          label: "Opportunity",
          value: pickScore(ss, ["sectionOpportunityScore"]),
        },
        { label: "Risk", value: pickScore(ss, ["sectionRiskScore"]) },
      ];

  return {
    sectionId,
    label: SECTION_TITLE[sectionId] || SECTION_LABEL[sectionId] || sectionId,
    section: isSummary ? null : (node as XFactorSection | null),
    scores,
    synopsis: pickText(ss, ["synopsis"]),
    highlightsBrief: pickText(ss, ["highlights_brief"]),
    lowlightsBrief: pickText(ss, ["lowlights_brief"]),
    risksBrief: pickText(ss, ["ro_brief"]),
    fitBrief: pickText(ss, ["orgRelevance"]),
    highlights: pointsFrom(pickList(node, ["keyHighlights"])),
    lowlights: pointsFrom(pickList(node, ["keyLowlights"])),
    actions: pointsFrom(pickList(node, ["keyActions"])),
    risks: pointsFrom(riskSignals(node)),
    opportunities: pointsFrom(opportunitySignals(node)),
    citations: citationsOf(node as XFactorSection | null),
    /**
     * The section's own composite. xfactor's briefing cards print this as
     * "OVERALL SCORE" — not health, which is what the matrix bubble uses.
     */
    composite: isSummary
      ? headlineScores(data).overall
      : pickScore(ss, ["sectionScore"]),
    confidence: pickScore(
      isDict(node?.commonMetadata) ? (node as Dict).commonMetadata : null,
      ["confidence_score"]
    ),
    risk: pickScore(ss, ["sectionRiskScore"]),
    opportunity: pickScore(ss, ["sectionOpportunityScore"]),
    missing: node == null,
  };
};

export const buildFullBriefing = (
  accountName: string,
  digestDate: string | null,
  data?: XFactorDigestData | null
): XFactorFullBriefing => ({
  accountName,
  digestDate,
  summary: getSummary(data),
  summaryDetail: getSectionDetail("summary", data),
  sections: presentSectionIds(data).map(id => getSectionDetail(id, data)),
});

/* ------------------------------------------------------------------ */
/* Briefing cards                                                       */
/* ------------------------------------------------------------------ */

export const buildBriefingCards = (
  data?: XFactorDigestData | null
): XFactorBriefingCard[] =>
  ["summary", ...presentSectionIds(data)].map(id => {
    const detail = getSectionDetail(id, data);
    return {
      sectionId: id,
      label: SECTION_LABEL[id] || id,
      scores:
        id === "summary"
          ? [
              { label: "Overall score", value: headlineScores(data).overall },
              { label: "Risk score", value: headlineScores(data).risk },
              { label: "Oppty score", value: headlineScores(data).opportunity },
            ]
          : [{ label: "Overall score", value: detail.composite }],
      synopsis: detail.synopsis,
      recommendations: detail.actions,
      risksAndOpportunities: detail.risksBrief,
      missing: detail.missing,
    };
  });

/* ------------------------------------------------------------------ */
/* Executive profiles                                                   */
/* ------------------------------------------------------------------ */

/**
 * Resolve a stakeholder name to the executive who carries the full profile.
 *
 * The Executive Brief and the leadership roster name the same people
 * differently — "Jamie Dimon" against "James Dimon", "Doug Petno" against
 * "Douglas B. Petno", "Mary Erdoes" against "Mary Callahan Erdoes". Exact
 * matching misses most of them, so this pairs on surname plus a three-letter
 * agreement on the given name, which is enough to separate colleagues while
 * still catching nicknames and dropped middle names.
 *
 * Returns null rather than guessing when nothing agrees.
 */
export const matchExecProfile = (
  name: string | null | undefined,
  profiles: XFactorExecProfile[]
): XFactorExecProfile | null => {
  const parts = (name || "")
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length < 2) return null;
  const [first, last] = [parts[0], parts[parts.length - 1]];

  return (
    profiles.find(p => {
      const other = (p.name || "")
        .toLowerCase()
        .replace(/[^a-z\s]/g, " ")
        .split(/\s+/)
        .filter(Boolean);
      if (other.length < 2) return false;
      if (other[other.length - 1] !== last) return false;
      const theirFirst = other[0];
      return (
        theirFirst === first ||
        theirFirst.startsWith(first) ||
        first.startsWith(theirFirst) ||
        (first.length >= 3 && theirFirst.slice(0, 3) === first.slice(0, 3))
      );
    }) || null
  );
};

/**
 * Executives live at `leadership.sectionSummary.keyContent.executives` — note
 * the nesting under `sectionSummary`, not the section's own empty `keyContent`.
 */
export const buildExecProfiles = (
  data?: XFactorDigestData | null
): XFactorExecProfile[] => {
  const leadership = getSection(data, "leadership") as Dict | null;
  const ss = isDict(leadership?.sectionSummary)
    ? ((leadership as Dict).sectionSummary as Dict)
    : null;
  const keyContent = isDict(ss?.keyContent)
    ? (ss as Dict).keyContent
    : leadership?.keyContent;
  const people = pickList(keyContent, ["executives", "leaders", "people"]);

  const attributeKeys: Array<[string, string[]]> = [
    ["Function", ["functional_role"]],
    ["Category", ["roleCategory"]],
    ["Org scope", ["org_scope"]],
    ["Rev/Budget", ["rev_budget"]],
    ["Board", ["board_exposure"]],
    // No Archetype chip — dropped from the profile card everywhere.
    ["Influence", ["influence_style"]],
    ["DISC", ["discProfile"]],
  ];

  return (
    people
      .map(p => ({
        // xfactor prints `full_name` — the payload also carries a shorter
        // colloquial `name` ("Jamie" vs "James") and picking that renders a
        // different person's name than the source shows.
        name: pickText(p, ["full_name", "name"]),
        title: pickText(p, ["title"]),
        since: pickText(p, ["tenure"]),
        tenure: (() => {
          const y = pickScore(p, ["tenure_years"]);
          return y == null ? null : `${y}y`;
        })(),
        influence: pickText(p, ["confidence"]),
        bio: stripRefMarkers(
          pickText(p, ["briefBio", "executive_profile_summary"])
        ),
        previous: (() => {
          const role = stripRefMarkers(pickText(p, ["previousRole"]));
          const company = stripRefMarkers(pickText(p, ["previousCompany"]));
          if (role && company) return `${role} @ ${company}`;
          return role || company;
        })(),
        education: stripRefMarkers(pickText(p, ["education"])),
        decisionMaking: pickText(p, ["decision_making_profile"]),
        attributes: attributeKeys
          .map(([label, keys]) => ({ label, value: pickText(p, keys) || "" }))
          .filter(a => a.value),
        refs: refsOf(p).length ? refsOf(p) : singleRef(p),
        disc: discOf(p),
        decisionAttributes: decisionAttributesOf(p),
        influenceStyle: influenceStyleOf(p),
      }))
      .filter(p => p.name)
      /**
       * The roster legitimately lists the same person twice — xfactor's payload
       * repeats an executive who holds two titles (Jeremy Barnum appears as CFO
       * under both the C-suite and the finance bench). Keep the first occurrence,
       * which is the one the section's own ordering leads with.
       */
      .filter((p, i, all) => {
        const key = (p.name || "").trim().toLowerCase();
        return (
          all.findIndex(o => (o.name || "").trim().toLowerCase() === key) === i
        );
      })
  );
};

/** `discProfile` — the four axes plus the style prose on the card's back. */
const discOf = (person: unknown): XFactorDisc | null => {
  const raw = pick(person, ["discProfile"]);
  if (!isDict(raw)) return null;
  const disc = {
    d: pickScore(raw, ["d"]),
    i: pickScore(raw, ["i"]),
    s: pickScore(raw, ["s"]),
    c: pickScore(raw, ["c"]),
    primaryType: pickText(raw, ["primaryType"]),
    confidence: pickScore(raw, ["confidenceLevel"]),
    communicationStyle: stripRefMarkers(pickText(raw, ["communicationStyle"])),
    decisionMakingStyle: stripRefMarkers(
      pickText(raw, ["decisionMakingStyle"])
    ),
  };
  // Any single axis can be null; a block with none of them is not a DISC.
  const hasAxis = [disc.d, disc.i, disc.s, disc.c].some(v => v != null);
  return hasAxis || disc.primaryType ? disc : null;
};

const attributesUnder = (node: unknown): XFactorDecisionAttribute[] =>
  pickList(node, ["attributes"])
    .map(a => ({
      attribute: pickText(a, ["attribute"]),
      value: pickText(a, ["value"]),
      confidence: pickText(a, ["confidence"]),
      // Evidence prose carries the same inline [c4][c7] markers the rest of the
      // payload does — xfactor strips them at render.
      evidence: stripRefMarkers(pickText(a, ["evidence"])),
    }))
    .filter(a => a.attribute);

const decisionAttributesOf = (person: unknown): XFactorDecisionAttribute[] =>
  attributesUnder(pick(person, ["decision_making_profile"]));

/**
 * `influence_style` — the second half of xfactor's DISC back face.
 *
 * It is an **object**, not a string: `{communication_style, attributes[]}`,
 * the same attribute shape as `decision_making_profile`. Reading it with
 * `pickText` yields null, which is why this section rendered as nothing at all
 * rather than as an obvious gap.
 */
const influenceStyleOf = (
  person: unknown
): {
  summary: string | null;
  attributes: XFactorDecisionAttribute[];
} | null => {
  const node = pick(person, ["influence_style"]);
  if (!isDict(node)) return null;
  const summary = stripRefMarkers(
    pickText(node, ["communication_style", "summary", "style"])
  );
  const attributes = attributesUnder(node);
  return summary || attributes.length ? { summary, attributes } : null;
};

/** Board composition and leadership changes, alongside the profiles. */
export const buildLeadershipExtras = (data?: XFactorDigestData | null) => {
  const leadership = getSection(data, "leadership") as Dict | null;
  const ss = isDict(leadership?.sectionSummary)
    ? ((leadership as Dict).sectionSummary as Dict)
    : null;
  const keyContent = isDict(ss?.keyContent)
    ? ((ss as Dict).keyContent as Dict)
    : null;
  const board = isDict(keyContent?.boardComposition)
    ? (keyContent as Dict).boardComposition
    : null;

  return {
    boardSize: pickScore(board, ["board_size"]),
    boardHighlights: asStrings(pick(board, ["highlights"])),
    changes: pointsFrom(pickList(keyContent, ["leadershipChanges"])),
  };
};

/* ------------------------------------------------------------------ */
/* Explainability                                                       */
/* ------------------------------------------------------------------ */

export const buildExplainPanes = (
  data?: XFactorDigestData | null
): XFactorExplainPane[] =>
  ["summary", ...presentSectionIds(data)].map(id => {
    const isSummary = id === "summary";
    const node = (
      isSummary ? getSummary(data) : getSection(data, id)
    ) as Dict | null;
    const meta = isDict(node?.commonMetadata)
      ? ((node as Dict).commonMetadata as Dict)
      : null;
    const ss = isDict(node?.sectionSummary)
      ? ((node as Dict).sectionSummary as Dict)
      : null;
    const detail = getSectionDetail(id, data);

    // Each score carries its own `explanation` — that is the real answer to
    // "how was this computed", not a generic description of the formula.
    const scoreExplanations = [
      ["Health", "sectionHealthScore"],
      // Fit is not surfaced anywhere in this port, so its rationale is dropped
      // here too rather than explaining a score the reader never sees.
      ["Opportunity", "sectionOpportunityScore"],
      ["Risk", "sectionRiskScore"],
      ["Composite", "sectionScore"],
    ]
      .map(([label, key]) => {
        const node2 = pick(ss, [key]);
        const explanation = pickText(node2, ["explanation"]);
        return explanation ? `${label}: ${explanation}` : "";
      })
      .filter(Boolean);

    const glanceWhy = isSummary
      ? pickText(
          (executiveBriefOf(data)?.situationAtAGlance ?? null) as Dict | null,
          ["whyTheseScores"]
        )
      : null;

    return {
      sectionId: id,
      label: SECTION_LABEL[id] || id,
      questions: [
        {
          id: "Q1",
          question: "How are various scores computed?",
          hint: "Each dimension carries the rationale the synthesis pass recorded for it.",
          answer: glanceWhy,
          bullets: scoreExplanations,
        },
        {
          id: "Q2",
          question:
            "How reliable and confident is the underlying data, and what are the key sources?",
          hint: "Freshness, confidence, analysis period, and the sources cited across this pane.",
          answer: pickText(meta, ["confidence_basis"]),
          bullets: [
            meta?.data_freshness ? `Freshness: ${meta.data_freshness}` : "",
            meta?.confidence_score != null
              ? `Confidence: ${meta.confidence_score}`
              : "",
            meta?.analysis_period
              ? `Analysis period: ${meta.analysis_period}`
              : "",
            meta?.last_updated ? `Last updated: ${meta.last_updated}` : "",
          ].filter(Boolean) as string[],
        },
        {
          id: "Q3",
          question: "What evidence and citations back the narrative?",
          hint: "The citations behind the narrative, and what each source covers.",
          answer: detail.citations.length
            ? `${detail.citations.length} cited source${
                detail.citations.length === 1 ? "" : "s"
              }.`
            : null,
          bullets: pickList(meta, ["sources"])
            .map(s =>
              [
                pickText(s, ["name"]),
                pickText(s, ["coverage"]),
                pickText(s, ["timestamp"]),
              ]
                .filter(Boolean)
                .join(" · ")
            )
            .filter(Boolean),
        },
      ],
    };
  });

/* ------------------------------------------------------------------ */
/* Formatting                                                           */
/* ------------------------------------------------------------------ */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * ISO date → `31-Jul-2026`, the format xfactor prints everywhere.
 *
 * Parsed by hand rather than via `new Date(iso)`: that parses a bare
 * `YYYY-MM-DD` as UTC midnight and then renders it in local time, which moves
 * the date back a day for anyone west of Greenwich.
 */
export const formatDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return iso;
  return `${m[3]}-${month}-${m[1]}`;
};
